import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserMe } from '@/types';

interface OriginalSession {
  accessToken: string;
  refreshToken: string;
  user: UserMe;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: UserMe | null;
  isAuthenticated: boolean;
  // When impersonating, holds the admin's original session for one-click exit.
  originalSession: OriginalSession | null;

  setTokens: (access: string, refresh: string) => void;
  setUser: (user: UserMe) => void;
  login: (access: string, refresh: string, user: UserMe) => void;
  logout: () => void;
  hasPermission: (perm: string) => boolean;

  startImpersonation: (access: string, refresh: string, targetUser: UserMe) => void;
  stopImpersonation: () => void;
  isImpersonating: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      originalSession: null,

      setTokens: (access, refresh) => {
        set({ accessToken: access, refreshToken: refresh });
      },

      setUser: (user) => {
        set({ user });
      },

      login: (access, refresh, user) => {
        set({
          accessToken: access,
          refreshToken: refresh,
          user,
          isAuthenticated: true,
          originalSession: null,
        });
      },

      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          originalSession: null,
        });
      },

      hasPermission: (perm: string) => {
        const { user } = get();
        if (!user) return false;
        if (user.is_system_admin) return true;
        return user.permissions.includes(perm);
      },

      startImpersonation: (access, refresh, targetUser) => {
        const state = get();
        // Capture the admin's tokens only on first impersonation start.
        const original = state.originalSession ?? (
          state.accessToken && state.refreshToken && state.user
            ? {
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                user: state.user,
              }
            : null
        );
        set({
          accessToken: access,
          refreshToken: refresh,
          user: targetUser,
          isAuthenticated: true,
          originalSession: original,
        });
      },

      stopImpersonation: () => {
        const { originalSession } = get();
        if (!originalSession) return;
        set({
          accessToken: originalSession.accessToken,
          refreshToken: originalSession.refreshToken,
          user: originalSession.user,
          isAuthenticated: true,
          originalSession: null,
        });
      },

      isImpersonating: () => get().originalSession !== null,
    }),
    {
      name: 'mymaintenances-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        originalSession: state.originalSession,
      }),
    },
  ),
);
